<template>
  <main :class="{isBlackPage: String($route.name).startsWith('Videos_Unlock'), isPosterViewer,
    isStandalone: $route.meta.isStandalone}">
    <router-view v-slot="{Component}">
      <component :is="Component" v-if="$route.name === 'Live'" :key="$route.path" />
      <component :is="Component" v-else-if="$route.path.includes('Video')" :key="$route.fullPath" />
      <keep-alive v-else>
        <component :is="Component" :key="getPageKey($route)" />
      </keep-alive>
    </router-view>
    <Warning />
    <TestClockOff />
    <GdprConsent v-if="!isTestSite && $route.query.pdf === undefined" /><!-- test sites: never covering the test-site banner -->
    <AppModalDialogs />
    <SpeakerDialog />
  </main>
</template>

<script setup>
const log = (msg) => console.log(`Main: ${msg}`); // eslint-disable-line
import {computed, inject, onMounted, provide, watch} from "vue";
import Warning from "@/components/Popup/Warning.vue";
import TestClockOff from "@/components/TestClockOff.vue";
import GdprConsent from "@/components/GdprConsent.vue";
import {isTestSite} from "@/composables/isTestSite";
import AppModalDialogs from "@/components/App/Modal/Dialogs";
import SpeakerDialog from "@/components/People/SpeakerDialog.vue";
import {useIsSubscribed} from "@/composables/useIsSubscribed.js";
import useUserSafe from "@/composables/useUserSafe";
import {getPosterAddress} from "@/composables/usePosters.js";
import {useRoute} from "vue-router";

const displayWidth = inject("displayWidth");
const route = useRoute();
// a poster's own page: no site header above it, so little space above its own (PosterPage.vue)
const isPosterViewer = computed(() => Boolean(getPosterAddress(route)));
// routes flagged isSamePageAcrossParams (e.g. /collective/:videoId) keep a single page instance
// across video IDs and screen rotation: a second instance would open a second video player
const getPageKey = (route) => {
  if (route.meta.isSamePageAcrossParams) return String(route.name);
  // a poster's page: the same across widths, or going full screen (a wider window) would
  // rebuild it, and the browser would leave full screen at once
  if (getPosterAddress(route)) return route.path;
  return `${route.path}+${displayWidth.value}`;
};
const isSubscribed = inject("isSubscribed");
const remount = inject("remount");

const {isLoaded, user, isSignedIn} = useUserSafe();
const isSubscribedUpdated = useIsSubscribed(user, isSignedIn);

provide("user", user);
provide("isSignedIn", isSignedIn);

onMounted(() => (remount.value ? log("re-mounted()") : log("mounted()")));

watch(
  isLoaded,
  (loaded) => {
    if (!loaded) return log("Clerk not yet loaded");
    log("Clerk is loaded");
    if (!user.value) return log("User object is falsy");
    log("Clerk user object is truthy");
    if (typeof user.value.reload !== "function") {
      return log("clerk user object has no reload()");
    }
  },
  {immediate: true}
);

watch(
  isSignedIn,
  (signedIn) => {
    if (!signedIn) return log("Clerk user is not yet signedIn");
    log("Clerk user is signed in");
  },
  {immediate: true}
);

watch(
  isSubscribedUpdated,
  (subscribedUpdated) => {
    if (!subscribedUpdated) {
      isSubscribed.value = false;
      return log("Clerk user is not subscribed");
    }
    log("Clerk user is subscribed");
    isSubscribed.value = true;
  },
  {immediate: true}
);
</script>

<style scoped>
main {
  min-height: 70vh;
  padding: 1.5rem 1rem;
  background-color: var(--background-color);
  color: var(--text-color);
}
.narrow main {
  padding: 1.5rem 0.8rem;
}
/* /Videos/Unlock is designed on black: the page keeps its margins, in black */
main.isBlackPage {
  background-color: black;
  color: white;
}
/* a standalone page (/one-pager): its own full width, nothing around it */
main.isStandalone {
  padding: 0;
  min-height: 0;
  background-color: black;
}
main.isPosterViewer {
  min-height: 0;
  padding-top: 0.25rem;
  padding-bottom: 0.5rem;
}
@import "@/styles/show-hide-stage.css";
</style>
